import { useRef, useState } from "react";
import CalendarIcon from "./CalendarIcon";
import PayPalCheckout from "./PayPalCheckout";
import { assets, bookingPayment } from "../data/siteData";
import { createBooking } from "../services/bookings";

export default function BookingSection() {
  const [formError, setFormError] = useState("");
  const [invalidFields, setInvalidFields] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedBookingId, setSavedBookingId] = useState("");
  const [currentLevel, setCurrentLevel] = useState("");
  const [isBeginner, setIsBeginner] = useState(false);
  const [suggestedCallTime, setSuggestedCallTime] = useState("");
  const [validationAttempt, setValidationAttempt] = useState(0);
  const suggestedCallTimeRef = useRef(null);

  async function handleBookingSubmit(event) {
    event.preventDefault();
    setFormError("");
    setSavedBookingId("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    const bookingDetails = Object.fromEntries(formData.entries());
    const invalidBookingFields = getInvalidBookingFields(bookingDetails);

    if (invalidBookingFields.length > 0) {
      setInvalidFields(invalidBookingFields);
      setValidationAttempt((attempt) => attempt + 1);
      setFormError(getValidationErrorMessage(invalidBookingFields));
      focusFirstInvalidField(form, invalidBookingFields);
      return;
    }

    setInvalidFields([]);
    setIsSubmitting(true);

    try {
      const booking = await createBooking(bookingDetails);

      saveBookingDraft(bookingDetails, { bookingId: booking.id });

      setSavedBookingId(booking.id);
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      setFormError(getBookingErrorMessage(error));

      saveBookingDraft(bookingDetails, { saveError: error.message });

      console.error("Booking submission failed", error);
    }
  }

  function openSuggestedCallPicker() {
    const input = suggestedCallTimeRef.current;

    if (!input) {
      return;
    }

    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }

    input.focus();
  }

  function handleCurrentLevelChange(event) {
    const nextLevel = event.target.value;

    setCurrentLevel(nextLevel);
    setIsBeginner(isBeginnerLevel(nextLevel));
    clearInvalidField("currentLevel");
  }

  function handleBeginnerChange(event) {
    const isChecked = event.target.checked;

    setIsBeginner(isChecked);

    if (isChecked) {
      setCurrentLevel((level) =>
        isBeginnerLevel(level) ? level : "Complete beginner"
      );
      clearInvalidField("currentLevel");
      return;
    }

    if (isBeginnerLevel(currentLevel)) {
      setCurrentLevel("");
    }
  }

  function clearInvalidField(fieldName) {
    setInvalidFields((fields) => fields.filter((field) => field !== fieldName));
  }

  function getFieldClassName(fieldName) {
    const isInvalid = invalidFields.includes(fieldName);

    return isInvalid
      ? `field-invalid field-shake-${validationAttempt % 2}`
      : undefined;
  }

  function isFieldInvalid(fieldName) {
    return invalidFields.includes(fieldName);
  }

  if (savedBookingId) {
    return (
      <section id="book" className="booking-section" aria-labelledby="payment-title">
        <div className="booking-panel booking-payment-panel">
          <PayPalCheckout bookingId={savedBookingId} />
        </div>
      </section>
    );
  }

  return (
    <section id="book" className="booking-section" aria-labelledby="book-title">
      <div className="booking-panel">
        <div className="booking-copy">
          <p className="eyebrow">Book your all-levels strategy session</p>
          <h2 id="book-title">Ready To Start Or Level Up?</h2>
          <p>
            {bookingPayment.displayAmount} includes a beginner-friendly playing
            assessment, documented learning roadmap, and a 1 hour private
            strategy session tailored to your current level.
          </p>
        </div>

        <div className="booking-flow">
          <form className="booking-form" onSubmit={handleBookingSubmit} noValidate>
            {formError ? (
              <div className="form-alert form-alert-error" role="alert">
                {formError}
              </div>
            ) : null}

            <div className="field-grid">
              <label className={getFieldClassName("fullName")}>
                <span>Full Name</span>
                <input
                  name="fullName"
                  type="text"
                  autoComplete="name"
                  aria-invalid={isFieldInvalid("fullName")}
                  onInput={() => clearInvalidField("fullName")}
                  required
                />
              </label>

              <label className={getFieldClassName("email")}>
                <span>Email Address</span>
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={isFieldInvalid("email")}
                  onInput={() => clearInvalidField("email")}
                  required
                />
              </label>
            </div>

            <div className="field-grid">
              <label className={getFieldClassName("currentLevel")}>
                <span>Current Playing Level</span>
                <select
                  name="currentLevel"
                  aria-invalid={isFieldInvalid("currentLevel")}
                  onChange={handleCurrentLevelChange}
                  value={currentLevel}
                  required
                >
                  <option value="" disabled>
                    Select your level
                  </option>
                  <option value="Complete beginner">
                    Complete beginner - I am just starting
                  </option>
                  <option value="Beginner">
                    Beginner - I know a few chords
                  </option>
                  <option value="Self-taught">
                    Self-taught - I need structure
                  </option>
                  <option value="Intermediate">
                    Intermediate - I want stronger gospel movement
                  </option>
                  <option value="Advanced">
                    Advanced - I want deeper refinement
                  </option>
                </select>
              </label>

              <label className={getFieldClassName("whatsappNumber")}>
                <span>WhatsApp Number</span>
                <input
                  name="whatsappNumber"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="+27 82 123 4567"
                  aria-invalid={isFieldInvalid("whatsappNumber")}
                  onInput={() => clearInvalidField("whatsappNumber")}
                  required
                />
              </label>

              <label className={`field-full ${getFieldClassName("suggestedCallTime") || ""}`}>
                <span>Coaching Call Date and Time</span>
                <span className={`input-with-icon${suggestedCallTime ? " has-value" : ""}`}>
                  <span className="input-icon-button" aria-hidden="true">
                    <CalendarIcon />
                  </span>
                  <span className="datetime-placeholder">Select date and time</span>
                  <input
                    ref={suggestedCallTimeRef}
                    name="suggestedCallTime"
                    type="datetime-local"
                    aria-label="Date and time"
                    onClick={openSuggestedCallPicker}
                    aria-invalid={isFieldInvalid("suggestedCallTime")}
                    onChange={(event) => {
                      setSuggestedCallTime(event.target.value);
                      clearInvalidField("suggestedCallTime");
                    }}
                    required
                  />
                </span>
              </label>
            </div>

            <label className="beginner-checkbox">
              <input
                name="isBeginner"
                type="checkbox"
                checked={isBeginner}
                onChange={handleBeginnerChange}
                value="yes"
              />
              <span>
                I am a beginner. Please start with the foundations.
              </span>
            </label>

            <div className="payment-method-selector">
              <h3>Select payment method</h3>
              <div className="payment-card" aria-label="Payment options">
                <div>
                  <span className="payment-icon" aria-hidden="true">
                    <img src={assets.paypalLogo} alt="" />
                  </span>
                  <span className="payment-label">Available now</span>
                  <strong>PayPal {bookingPayment.displayAmount}</strong>
                </div>
                <div>
                  <span className="payment-icon payment-icon-muted" aria-hidden="true">
                    <img src={assets.cardIcon} alt="" />
                  </span>
                  <span className="payment-label">Coming soon</span>
                  <strong>Card</strong>
                </div>
              </div>
            </div>

            <div className="booking-actions">
              <button
                className="button button-primary"
                type="submit"
                disabled={isSubmitting || Boolean(savedBookingId)}
              >
                {isSubmitting ? (
                  <>
                    <span className="button-loader" aria-hidden="true" />
                    Saving Booking...
                  </>
                ) : (
                  "Continue with PayPal"
                )}
              </button>
              <span>Card payments coming soon.</span>
            </div>

          </form>

          <p className="booking-note">
            Whether you are touching gospel chords for the first time or
            refining your sound, this session meets you at your level and gives
            you a clear next step. Your booking details are saved first, then
            you will continue to a payment page for the {bookingPayment.displayAmount} payment.
          </p>
        </div>
      </div>
    </section>
  );
}

function getBookingErrorMessage(error) {
  if (error?.code === "permission-denied") {
    return "We couldn't save your booking yet. Please try again in a moment.";
  }

  return (
    error?.message ||
    "We couldn't save your booking yet. Please check your connection and try again."
  );
}

const REQUIRED_BOOKING_FIELDS = [
  {
    name: "fullName",
    label: "Full Name",
    isValid: (value) => cleanText(value).length >= 2
  },
  {
    name: "email",
    label: "Email Address",
    isValid: (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleanText(value))
  },
  {
    name: "whatsappNumber",
    label: "WhatsApp Number",
    isValid: (value) => {
      const phoneNumber = cleanText(value);

      return (
        phoneNumber.length >= 7 &&
        phoneNumber.length <= 32 &&
        /^[0-9+()\-\s]+$/.test(phoneNumber)
      );
    }
  },
  {
    name: "currentLevel",
    label: "Current Playing Level",
    isValid: (value) => cleanText(value).length > 0
  },
  {
    name: "suggestedCallTime",
    label: "Coaching Call Date and Time",
    isValid: (value) => cleanText(value).length > 0
  }
];

function getInvalidBookingFields(bookingDetails) {
  return REQUIRED_BOOKING_FIELDS.filter(
    (field) => !field.isValid(bookingDetails[field.name])
  ).map((field) => field.name);
}

function getValidationErrorMessage(invalidFieldNames) {
  const invalidLabels = REQUIRED_BOOKING_FIELDS.filter((field) =>
    invalidFieldNames.includes(field.name)
  ).map((field) => field.label);

  return `Please fix the highlighted field${
    invalidLabels.length === 1 ? "" : "s"
  }: ${formatList(invalidLabels)}.`;
}

function focusFirstInvalidField(form, invalidFieldNames) {
  const firstInvalidField = form.elements[invalidFieldNames[0]];

  if (firstInvalidField && typeof firstInvalidField.focus === "function") {
    firstInvalidField.focus();
  }
}

function formatList(items) {
  if (items.length <= 1) {
    return items[0] || "";
  }

  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }

  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function cleanText(value = "") {
  return String(value).trim();
}

function isBeginnerLevel(level) {
  return level === "Complete beginner" || level === "Beginner";
}

function saveBookingDraft(bookingDetails, extraDetails) {
  sessionStorage.setItem(
    "gospelKeysBookingDraft",
    JSON.stringify({
      ...bookingDetails,
      ...extraDetails,
      submittedAt: new Date().toISOString()
    })
  );
}
